import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  login as doLogin,
  logout as doLogout,
  getCurrentUser,
} from "../services/auth";
import { useNavigate } from "react-router-dom";
import {
  clearAuthSession,
  ensureActiveSession,
  getStoredToken,
  isTokenExpired,
  setupSessionExpiryListeners,
} from "../utils/authSession";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getCurrentUser());
  const navigate = useNavigate();
  const [token, setToken] = useState(() => getStoredToken());

  useEffect(() => {
    const storedToken = getStoredToken();
    if (!storedToken || isTokenExpired(storedToken)) {
      clearAuthSession();
      setToken("");
      setUser(null);
      return;
    }
    setUser(getCurrentUser());
  }, [token]);

  useEffect(() => {
    ensureActiveSession();
    const removeListeners = setupSessionExpiryListeners();

    const onSessionExpired = () => {
      setToken("");
      setUser(null);
    };
    window.addEventListener("auth:session-expired", onSessionExpired);

    return () => {
      removeListeners();
      window.removeEventListener("auth:session-expired", onSessionExpired);
    };
  }, []);

  const login = async (email, password) => {
    const res = await doLogin(email, password);
    setToken(res.token);
    setUser(res.user);
    return res;
  };

  const logout = () => {
    doLogout();
    setToken("");
    setUser(null);
    navigate("/login");
  };

  const value = useMemo(() => ({ user, token, login, logout }), [user, token]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
